import {uniqBy} from 'lodash'

/**
 * Search helpers for the "Clientes con servicios" list (POST admin/supervisors on the legacy Mapit2 API).
 *
 * The default list only loads the most recent supervisors of a group (ordered by id desc), so a text search
 * must also query the server to find older supervisors. Mapit2 only supports these text filters:
 * - login: prefix LIKE on the account login (escaped server side)
 * - idNumber: exact match on Detail.$.Document (NOT escaped server side)
 * - name / surname: case-insensitive prefix on Detail.$.Name / Detail.$.Surname (NOT escaped server side)
 * There is no phone filter, so phone searches can only be resolved over the already loaded rows.
 */

const MIN_LENGTH = 3

export type SupervisorTextFilter = {
  login?: string
  idNumber?: string
  name?: string
  surname?: string
}

// DNI (12345678Z), NIE (X1234567L) and CIF/NIF of companies (B12345678)
const SPANISH_DOCUMENT_REGEX = /^(\d{8}[A-Z]|[XYZ]\d{7}[A-Z]|[ABCDEFGHJNPQRSUVW]\d{7}[0-9A-J])$/
// Portuguese NIF: 9 digits
const PORTUGUESE_NIF_REGEX = /^\d{9}$/
const PHONE_REGEX = /^\+?[\d\s().-]+$/
const MIN_PHONE_DIGITS = 6

const stripSeparators = (value: string) => value.replace(/[\s().-]/g, '')

/**
 * name, surname and idNumber are interpolated into the SQL string without escaping on Mapit2,
 * so a single quote would break the query. As name/surname are prefix searches, cutting the term
 * at the first quote keeps the results a superset of the expected ones.
 */
function sanitizePrefix(value: string): string {
  return value.split("'")[0].trim()
}

function isPhoneLike(value: string): boolean {
  return PHONE_REGEX.test(value) && value.replace(/\D/g, '').length >= MIN_PHONE_DIGITS
}

function dedupeQueries(queries: SupervisorTextFilter[]): SupervisorTextFilter[] {
  const valid = queries.filter(query => Object.values(query).every(Boolean))
  return uniqBy(valid, query => JSON.stringify(query))
}

/**
 * Translates the text typed in the search box into the server queries to run (their results must be merged).
 * Returns no queries when the term is too short or can only be matched client side (phone numbers).
 */
export function buildSupervisorSearchQueries(rawSearch: string | null | undefined): SupervisorTextFilter[] {
  const search = (rawSearch || '').trim().replace(/\s+/g, ' ')

  if (search.length < MIN_LENGTH) return []

  if (search.includes('@')) return [{login: search.toLowerCase()}]

  // Only letters, digits and separators get here, so the document is safe to send
  const document = stripSeparators(search).toUpperCase()
  if (SPANISH_DOCUMENT_REGEX.test(document) || PORTUGUESE_NIF_REGEX.test(document)) {
    // 9 digits can also be a phone number: that case is covered by the local filter over the loaded rows
    return [{idNumber: document}]
  }

  if (isPhoneLike(search)) return []

  const words = search.split(' ')
  if (words.length === 1) {
    const word = sanitizePrefix(search)
    return dedupeQueries([{name: word}, {surname: word}, {login: search.toLowerCase()}])
  }

  // Every split between name and surname: "José Luis García" can be José + Luis García or José Luis + García
  const nameSurnameSplits = words.slice(1).map((_, i) => ({
    name: sanitizePrefix(words.slice(0, i + 1).join(' ')),
    surname: sanitizePrefix(words.slice(i + 1).join(' ')),
  }))
  return dedupeQueries([
    // Compound names ("José Luis") and compound surnames ("García López")
    {name: sanitizePrefix(search)},
    {surname: sanitizePrefix(search)},
    ...nameSurnameSplits,
  ])
}

type SupervisorRow = {
  ownerId?: number | string
  ownerLogin?: string
  fullName?: string
  phone?: string | number
  ownerDetail?: {Document?: string} | null
}

/** True when the server queries had to be widened (terms cut at a quote), so their results need a local check */
export function isSupervisorSearchWidened(rawSearch: string): boolean {
  return !(rawSearch || '').includes('@') && (rawSearch || '').includes("'")
}

/**
 * Client side filter over already loaded rows (kept for phone numbers and partial "contains" matches).
 * The term is normalized once and the returned predicate is applied to every row.
 */
export function createSupervisorMatcher(rawSearch: string): (row: SupervisorRow) => boolean {
  const search = (rawSearch || '').trim().toLowerCase()
  if (!search) return () => true

  const phoneSearch = stripSeparators(search)
  const documentSearch = search.toUpperCase()

  return row =>
    Boolean(
      row.ownerLogin?.toLowerCase().includes(search) ||
      row.fullName?.toLowerCase().includes(search) ||
      (phoneSearch && stripSeparators(`${row.phone ?? ''}`).includes(phoneSearch)) ||
      row.ownerDetail?.Document?.toUpperCase().includes(documentSearch),
    )
}

/** Merges several result lists keeping the first occurrence of each supervisor (by ownerId) */
export function mergeSupervisors<T extends SupervisorRow>(...lists: T[][]): T[] {
  return uniqBy(lists.flat(), item => `${item.ownerId}`)
}
